<template>
  <PageHero
    :title="$t('hero.title_developer')"
    :breadcrumbsTop="[
      { label: $t('hero.breadcrumbs.home'), link: '/' },
      { label: $t('hero.breadcrumbs.developers') }
    ]"
  />
  <section class="service-details-section ptb-100">
    <div class="container">
      <div class="row">
        <!-- Левая часть -->
        <div class="col-lg-8 col-md-8">
          <div class="service-details-wrap">
            <!-- Баннер -->
            <img
              :src="developer?.banner || 'https://via.placeholder.com/1200x300'"
              :alt="$t('developer.banner_alt')"
              class="img-fluid rounded shadow-sm lazy-img"
              loading="lazy"
              @load="onImageLoad"
            />

            <!-- О застройщике -->
            <div class="services-detail-content mt-4">
              <div class="section-heading">
                <h4>{{ developer?.name_multilang?.[currentLocale] || developer?.name }}</h4>
                <div class="section-heading-line-left"></div>
              </div>
             <p v-if="developer?.description_multilang?.[currentLocale]" v-html="developer.description_multilang[currentLocale]"></p>

              <!-- Основная информация -->
              <div class="row mt-4">
                <div class="col-md-6">
                  <ul class="list-unstyled">
                    <li class="py-1" v-if="developer?.established_year">
                      <strong>{{ $t('developer.fields.founded_year') }}:</strong> {{ developer.established_year }}
                    </li>
                    <li class="py-1" v-if="developer?.country">
                      <strong>{{ $t('developer.fields.country') }}:</strong> {{ developer.country }}
                    </li>
                    <li class="py-1" v-if="developer?.project_types">
                      <strong>{{ $t('developer.fields.project_types') }}:</strong> {{ developer.project_types }}
                    </li>
                    <li class="py-1" v-if="developer?.locations?.length">
                      <strong>{{ $t('developer.fields.locations') }}:</strong> {{ developer.locations.length }}
                    </li>
                  </ul>
                </div>
                <div class="col-md-6" v-if="developer?.documents?.length">
                  <ul class="list-unstyled">
                    <li
                      class="py-1"
                      v-for="doc in developer.documents"
                      :key="doc"
                    >
                      <i class="ti-folder text-secondary me-2"></i>
                      <a :href="doc" target="_blank">{{ doc.split('/').pop() }}</a>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- Галерея -->
              <div v-if="developer?.gallery_images?.length" class="mt-5">
                <div class="section-heading">
                  <h5>{{ $t('developer.gallery') }}</h5>
                  <div class="section-heading-line-left"></div>
                </div>
                <div class="row">
                  <div
                    class="col-md-4 mb-3"
                    v-for="(img, index) in developer.gallery_images"
                    :key="index"
                  >
                    <img
                      :src="img"
                      :alt="$t('developer.gallery_image_alt')"
                      class="img-fluid rounded shadow-sm lazy-img"
                      loading="lazy"
                      @load="onImageLoad"
                    />
                  </div>
                </div>
              </div>

              <!-- Видео -->
              <div v-if="developer?.video_urls?.length" class="mt-5">
                <div class="section-heading">
                  <h5>{{ $t('developer.video') }}</h5>
                  <div class="section-heading-line-left"></div>
                </div>
                <div class="row">
                  <div
                    class="col-md-6 mb-3"
                    v-for="(video, idx) in developer.video_urls"
                    :key="idx"
                  >
                    <iframe
                      class="w-100 rounded shadow-sm"
                      height="300"
                      :src="formatYoutubeUrl(video)"
                      frameborder="0"
                      allowfullscreen
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Правая часть -->
        <div class="col-lg-4 col-md-4">
          <div class="sidebar-right pl-4">
            <!-- Контакты -->
            <aside class="widget bg-light rounded shadow-sm p-4 mb-4">
              <h5 class="text-center mb-3">{{ $t('developer.contacts') }}</h5>
              <ul class="list-unstyled text-center">
                <li v-if="developer?.phone" class="mb-2">
                  <i class="ti-mobile text-primary me-2"></i> {{ developer.phone }}
                </li>
                <li v-if="developer?.email" class="mb-2">
                  <i class="ti-email text-primary me-2"></i> {{ developer.email }}
                </li>
                <li v-if="developer?.website" class="mb-2">
                  <i class="ti-world text-primary me-2"></i>
                  <a :href="developer.website" target="_blank">{{ developer.website }}</a>
                </li>
              </ul>
            </aside>

            <!-- Логотип -->
            <aside class="widget text-center">
              <img
                :src="developer?.logo || '/assets/property-placeholder.png'"
                :alt="$t('developer.logo_alt')"
                class="img-fluid rounded shadow-sm lazy-img"
                loading="lazy"
                @load="onImageLoad"
              />
            </aside>
          </div>
        </div>

        <!-- Жилые комплексы -->
        <div class="col-12 mt-5" v-if="residentialComplexes?.length">
          <div class="section-heading">
            <h5>  {{ $t('developer.residential_complexes_from', { name: developer?.name }) }}</h5>
            <div class="section-heading-line-left"></div>
          </div>
          <ResidentialComplexCards :complexes="residentialComplexes" />
        </div>

        <!-- Объекты недвижимости -->
        <div class="col-12 mt-5" v-if="properties?.length">
          <div class="section-heading">
            <h5>{{ $t('developer.properties_from', { name: developer?.name }) }}</h5>
            <div class="section-heading-line-left"></div>
          </div>
          <PropertyCards :properties="properties" />
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import PropertyCards from '../pages/property_list/PropertyCards.vue'
import ResidentialComplexCards from '../pages/property_list/ResidentialComplexCards.vue'
import PageHero from '../components/PageHero.vue'

const { locale } = useI18n()

const developer = ref(null)
const residentialComplexes = ref([])
const properties = ref([])

const onImageLoad = (event) => {
  event.target.classList.add('loaded')
}

const loadData = async () => {
  try {
    const id = window.location.pathname.split('/').pop()
    const headers = { 'X-Locale': locale.value }

    const [developerRes, propertiesRes, complexesRes] = await Promise.all([
      fetch(`/api/v1/developers/${id}.json`, { headers }),
      fetch(`/api/v1/developers/${id}/properties.json`, { headers }),
      fetch(`/api/v1/developers/${id}/residential_complexes.json`, { headers })
    ])

    developer.value = await developerRes.json()

    const propsData = await propertiesRes.json()
    properties.value = propsData.properties || []

    const complexesData = await complexesRes.json()
    residentialComplexes.value = (complexesData.residential_complexes || complexesData).map(rc => ({
      ...rc,
      developer_id: developer.value.id
    }))
  } catch (err) {
    console.error('Ошибка загрузки:', err)
  }
}

onMounted(loadData)

// следим за сменой языка и перезагружаем данные
watch(locale, () => {
  loadData()
})

const formatYoutubeUrl = (url) => {
  return url.replace('watch?v=', 'embed/').split('&')[0]
}
</script>


<style scoped>
.lazy-img {
  opacity: 0;
  transition: opacity 0.5s ease-in;
}
.lazy-img.loaded {
  opacity: 1;
}
</style>
